// Cross-resource link helpers. Reads/writes the `resource_links` Mongo
// collection so chat channels, calendar events, and meet rooms can
// reference each other. Mongo-backed via sdk.resourceLinks.*; canonical
// (aType, aId) < (bType, bId) ordering enforced server-side.
//
// All functions support both direct calls and `el.call('fn', ...)` — root is
// inferred from `this.state.root` when the user passes a channel/event/room.
// Bare shared-library import, not a deep sdk.js subpath — see app.js's
// identical fix note (tickets/catalog-fixall.md P0). Deferred-call wrapper,
// not a module-scope destructure — see app.js's circular-init note.
import _wsShared from '@symbo-ls/workspace-shared'
const shellGetSDK = (...args) => _wsShared.functions.getSDK(...args)

export const RESOURCE_TYPE = Object.freeze({
  CHAT_CHANNEL: 'chat_channel',
  MEET_ROOM: 'meet_room',
  CALENDAR_EVENT: 'calendar_event'
})
const _VALID_TYPES = new Set(Object.values(RESOURCE_TYPE))

const _asId = (x) => (x == null ? null : String(x))

// Per-(links array identity) cache for getLinkedResources results. The cache
// is invalidated automatically whenever `root.resourceLinks` is replaced
// (link/unlink optimistic updates always produce a new array reference).
const _linksCache = new WeakMap()

export async function linkResources(aType, aId, bType, bId) {
  const root = this?.state?.root
  const me = root?.currentUserEmail
  if (!aType || aId == null || !bType || bId == null) return null
  if (aType === bType && _asId(aId) === _asId(bId)) return null
  try {
    // Server canonicalizes (aType, aId) < (bType, bId) ordering and is
    // idempotent — duplicate pair returns existing row with existed:true.
    const result = await shellGetSDK().execute('resourceLinks', 'create', {
      payload: { aType, aId: _asId(aId), bType, bId: _asId(bId), createdBy: me }
    })
    const data =
      result?.doc ??
      (Array.isArray(result?.data) ? result.data[0] : (result?.data ?? result ?? null))
    if (!data) return null
    if (root) {
      const cur = root.resourceLinks || []
      const matchKey = data._id || data.id
      const exists = cur.some((r) => (r._id || r.id) === matchKey)
      if (!exists) root.update({ resourceLinks: [...cur, data] })
    }
    return data
  } catch (err) {
    console.error('[linkResources]', err)
    return null
  }
}

export async function unlinkResources(aType, aId, bType, bId) {
  const root = this?.state?.root
  const sAId = _asId(aId)
  const sBId = _asId(bId)
  try {
    // Server canonicalizes ordering before lookup, so caller direction doesn't matter.
    await shellGetSDK().execute('resourceLinks', 'removeByPair', {
      payload: { aType, aId: sAId, bType, bId: sBId }
    })
    if (root) {
      const cur = root.resourceLinks || []
      root.update({
        resourceLinks: cur.filter(
          (r) =>
            !(
              (r.aType === aType && r.aId === sAId && r.bType === bType && r.bId === sBId) ||
              (r.aType === bType && r.aId === sBId && r.bType === aType && r.bId === sAId)
            )
        )
      })
    }
    return true
  } catch (err) {
    console.error('[unlinkResources]', err?.message || err)
    return false
  }
}

// Return every resource linked to (type, id). Memoized by
// `root.resourceLinks` array identity so repeated calls in the same render
// return a stable reference (cheap re-reads in reactive factories).
export function getLinkedResources(rootOrType, maybeId, maybeType) {
  let root, type, id
  if (typeof rootOrType === 'string') {
    root = this?.state?.root
    type = rootOrType
    id = maybeId
  } else {
    root = rootOrType
    type = maybeId
    id = maybeType
  }
  const links = (root && root.resourceLinks) || null
  if (!links) return []
  let byKey = _linksCache.get(links)
  if (!byKey) {
    byKey = new Map()
    _linksCache.set(links, byKey)
  }
  const sid = _asId(id)
  const key = `${type}\x00${sid}`
  const cached = byKey.get(key)
  if (cached) return cached
  const out = []
  for (const r of links) {
    const linkId = r._id ?? r.id
    if (r.aType === type && _asId(r.aId) === sid) {
      out.push({ type: r.bType, id: r.bId, linkId })
    } else if (r.bType === type && _asId(r.bId) === sid) {
      out.push({ type: r.aType, id: r.aId, linkId })
    }
  }
  byKey.set(key, out)
  return out
}

// Back-fill existing meet_rooms links (calendar_event_id / chat_channel_id)
// into resource_links. Safe to re-run — upserts on the unique key.
export async function backfillMeetRoomLinks() {
  const root = this?.state?.root
  const ops = []
  for (const r of root?.rooms || []) {
    if (r.calendarEventId != null) {
      ops.push(linkResources.call(this, 'meet_room', r.id, 'calendar_event', r.calendarEventId))
    }
    if (r.chatChannelId != null) {
      ops.push(linkResources.call(this, 'meet_room', r.id, 'chat_channel', r.chatChannelId))
    }
  }
  await Promise.all(ops)
}
